/**
 * SalesCadenceView — Weekly activity heatmap + Q1 Assignment Progress panel.
 * Shows whether each salesperson is hitting their weekly activity rhythm AND
 * their quarterly targets assigned from the Performance Hub.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Progress } from '../ui/progress';
import { RefreshCw, TrendingUp, TrendingDown, Minus, Target, Zap, Users, CheckCircle, AlertTriangle, Link2, Activity } from 'lucide-react';
import { toast } from 'sonner';

const SALES_TYPES = new Set(['Demo','RFP Submission','Proof of concept','Vendor Meeting','Product Presentation','POC','Vendor Presentation']);

const TYPE_COLORS = {
  Call:      'bg-blue-100 text-blue-700',
  Meeting:   'bg-amber-100 text-amber-700',
  Email:     'bg-purple-100 text-purple-700',
  Demo:      'bg-indigo-100 text-indigo-700',
  'To Do':   'bg-gray-100 text-gray-600',
  'Proof of concept': 'bg-cyan-100 text-cyan-700',
  default:   'bg-gray-100 text-gray-600',
};

function cellColor(count, target) {
  if (count === 0) return 'bg-gray-50 text-gray-300';
  const pct = count / target;
  if (pct >= 1.0) return 'bg-emerald-500 text-white';
  if (pct >= 0.6) return 'bg-emerald-200 text-emerald-800';
  if (pct >= 0.3) return 'bg-amber-200 text-amber-800';
  return 'bg-red-200 text-red-800';
}

function TrendIcon({ trend }) {
  if (trend === 'up')   return <TrendingUp className="h-3 w-3 text-emerald-500" />;
  if (trend === 'down') return <TrendingDown className="h-3 w-3 text-red-500" />;
  return <Minus className="h-3 w-3 text-gray-400" />;
}

function initials(name = '') {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : (name[0] || '?').toUpperCase();
}

const AVATAR_COLORS = ['#7C3AED','#DC2626','#D97706','#059669','#2563EB','#DB2777','#0891B2'];
function avatarColor(name = '') {
  const code = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

export function SalesCadenceView({ externalYearFilter, externalQuarterFilter, externalSalesOnly, onYearFilterChange, onQuarterFilterChange, onSalesOnlyChange }) {
  const [data, setData]               = useState(null);
  const [apData, setApData]           = useState(null); // assignment progress
  const [loading, setLoading]         = useState(true);
  const [apLoading, setApLoading]     = useState(true);
  const [weekCount, setWeekCount]     = useState('12');
  const [target, setTarget]           = useState('5');
  const [salesOnly, setSalesOnly]     = useState(externalSalesOnly ?? false);
  const [yearFilter, setYearFilter]   = useState(externalYearFilter || 'all');
  const [quarterFilter, setQuarterFilter] = useState(externalQuarterFilter || 'all');
  const [selectedSp, setSelectedSp]   = useState(null);
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(null);
  const [apPlanId, setApPlanId]       = useState('');   // filter by plan

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmAPI.getActivityCadence({
        weeks: parseInt(weekCount),
        weekly_target: parseInt(target),
        sales_only: salesOnly,
        year: yearFilter !== 'all' ? yearFilter : undefined,
        quarter: quarterFilter !== 'all' ? quarterFilter : undefined,
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load cadence data');
    } finally {
      setLoading(false);
    }
  }, [weekCount, target, salesOnly, yearFilter, quarterFilter]);

  const loadAssignmentProgress = useCallback(async () => {
    setApLoading(true);
    try {
      const params = {};
      if (apPlanId && apPlanId !== 'all') params.plan_id = apPlanId;
      const res = await crmAPI.getActivityAssignmentProgress(params);
      setApData(res.data);
    } catch {
      // non-fatal
    } finally {
      setApLoading(false);
    }
  }, [apPlanId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAssignmentProgress(); }, [loadAssignmentProgress]);
  useEffect(() => { if (externalYearFilter !== undefined) setYearFilter(externalYearFilter || 'all'); }, [externalYearFilter]);
  useEffect(() => { if (externalQuarterFilter !== undefined) setQuarterFilter(externalQuarterFilter || 'all'); }, [externalQuarterFilter]);
  useEffect(() => { if (externalSalesOnly !== undefined) setSalesOnly(!!externalSalesOnly); }, [externalSalesOnly]);

  const weeks = data?.weeks || [];
  const summary = data?.summary || [];
  const heatmap = data?.heatmap || {};
  const weeklyTarget = data?.weekly_target || 5;

  // Overall stats
  const totalActivities = data?.total_activities || 0;
  const avgWeekly = summary.length > 0
    ? Math.round(summary.reduce((s, x) => s + x.avg_per_week, 0) / summary.length * 10) / 10
    : 0;
  const onTargetThisWeek = summary.filter(s => s.current_week >= weeklyTarget).length;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="sales-cadence-view">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">Weeks:</span>
          <Select value={weekCount} onValueChange={v => { setWeekCount(v); setSelectedSp(null); }}>
            <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['4','8','12','16','24'].map(v => <SelectItem key={v} value={v}>{v}w</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">Target/week:</span>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="w-20 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['3','5','7','10','15','20'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">Year:</span>
          <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); onYearFilterChange?.(v); }}>
            <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {['2026','2025','2024'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">Quarter:</span>
          <Select value={quarterFilter} onValueChange={(v) => { setQuarterFilter(v); onQuarterFilterChange?.(v); }}>
            <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {['Q1','Q2','Q3','Q4'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => { const next = !salesOnly; setSalesOnly(next); onSalesOnlyChange?.(next); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${salesOnly ? 'bg-[#800000] text-white border-[#800000]' : 'bg-[#800000]/5 text-[#800000] border-[#800000]/20 hover:bg-[#800000]/10'}`}
          data-testid="sales-only-toggle-cadence"
          title="Sales Activities: Demo, RFP Submission, Proof of Concept, Vendor Meeting, Product Presentation, POC, Vendor Presentation"
        >
          <Zap className="h-3 w-3" /> Sales Activity Types Only
        </button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0 ml-auto" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Activities', value: totalActivities, sub: `last ${weekCount} weeks`, icon: Target, color: 'text-[#800000]', bg: 'bg-[#800000]/10' },
          { label: 'Avg / Person / Week', value: avgWeekly, sub: `target: ${weeklyTarget}`, icon: Users, color: avgWeekly >= weeklyTarget ? 'text-emerald-600' : 'text-amber-600', bg: avgWeekly >= weeklyTarget ? 'bg-emerald-50' : 'bg-amber-50' },
          { label: 'On Target This Week', value: `${onTargetThisWeek}/${summary.length}`, sub: 'salespersons', icon: CheckCircle, color: onTargetThisWeek === summary.length ? 'text-emerald-600' : 'text-amber-600', bg: 'bg-emerald-50' },
          { label: 'Active Salespersons', value: summary.length, sub: 'in period', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
        ].map(kpi => { const I = kpi.icon; return (
          <Card key={kpi.label}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${kpi.bg}`}><I className={`h-4 w-4 ${kpi.color}`} /></div>
                <div>
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
                  <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );})}
      </div>

      {/* Heatmap grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            Weekly Activity Heatmap
            <span className="ml-auto flex items-center gap-2 text-[10px] font-normal">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"/>{'>'}= target</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-200 inline-block"/>60–99%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-200 inline-block"/>30–59%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-200 inline-block"/>{'<'}30%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-50 border inline-block"/>none</span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="cadence-heatmap">
            <thead>
              <tr>
                <th className="text-left py-1.5 pr-3 font-semibold text-muted-foreground w-44 sticky left-0 bg-background">
                  Salesperson
                </th>
                {weeks.map(w => (
                  <th key={w.label} className={`text-center py-1.5 px-1 font-medium min-w-[44px] ${w.is_current ? 'text-[#800000]' : 'text-muted-foreground'}`}>
                    <div>{w.display}</div>
                    {w.is_current && <div className="text-[8px] text-[#800000] font-bold">NOW</div>}
                  </th>
                ))}
                <th className="text-center py-1.5 px-2 font-semibold text-muted-foreground">Avg/wk</th>
                <th className="text-center py-1.5 px-2 font-semibold text-muted-foreground">Hit%</th>
                <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Top Types</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((sp, rowIdx) => (
                <React.Fragment key={sp.salesperson}>
                  <tr
                    className={`border-t cursor-pointer hover:bg-muted/30 transition-colors ${selectedSp === sp.salesperson ? 'bg-muted/30' : ''}`}
                    onClick={() => {
                      const next = selectedSp === sp.salesperson ? null : sp.salesperson;
                      setSelectedSp(next);
                      if (next === null) setSelectedWeekIdx(null);
                    }}
                    data-testid={`cadence-row-${rowIdx}`}
                  >
                    {/* Salesperson */}
                    <td className="py-2 pr-3 sticky left-0 bg-background">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                          style={{ backgroundColor: avatarColor(sp.salesperson) }}>
                          {initials(sp.salesperson)}
                        </div>
                        <div>
                          <div className="font-medium truncate max-w-28" title={sp.salesperson}>
                            {sp.salesperson.split(' ').slice(0, 2).join(' ')}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <TrendIcon trend={sp.trend} />
                            <span className={`text-[10px] ${sp.current_week >= weeklyTarget ? 'text-emerald-600' : sp.current_week > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                              {sp.current_week} this wk
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Weekly cells */}
                    {weeks.map((w, wi) => {
                      const count = sp.weekly_counts[wi] || 0;
                      const cc = cellColor(count, weeklyTarget);
                      return (
                        <td key={w.label} className="py-2 px-1 text-center">
                          <button type="button" className={`mx-auto w-9 h-7 rounded flex items-center justify-center text-xs font-semibold ${cc} ${selectedSp === sp.salesperson && selectedWeekIdx === wi ? 'ring-2 ring-[#800000] ring-offset-1' : ''}`}
                            title={`${sp.salesperson}: ${count} activities week of ${w.display}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedSp(sp.salesperson); setSelectedWeekIdx(selectedSp === sp.salesperson && selectedWeekIdx === wi ? null : wi); }}
                            data-testid={`cadence-cell-${rowIdx}-${wi}`}>
                            {count || ''}
                          </button>
                        </td>
                      );
                    })}
                    {/* Summary cols */}
                    <td className="py-2 px-2 text-center">
                      <span className={`font-bold ${sp.avg_per_week >= weeklyTarget ? 'text-emerald-600' : sp.avg_per_week >= weeklyTarget * 0.6 ? 'text-amber-600' : 'text-red-500'}`}>
                        {sp.avg_per_week}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`font-bold text-xs ${sp.target_hit_rate_pct >= 80 ? 'text-emerald-600' : sp.target_hit_rate_pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                          {sp.target_hit_rate_pct}%
                        </span>
                        <div className="w-10 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${sp.target_hit_rate_pct >= 80 ? 'bg-emerald-500' : sp.target_hit_rate_pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${Math.min(sp.target_hit_rate_pct, 100)}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex flex-wrap gap-0.5">
                        {sp.type_breakdown.slice(0, 3).map(t => (
                          <span key={t.type} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium ${TYPE_COLORS[t.type] || TYPE_COLORS.default}`}>
                            {SALES_TYPES.has(t.type) && <Zap className="h-2 w-2" />}
                            {t.type} {t.count}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded detail row */}
                  {selectedSp === sp.salesperson && (
                    <tr className="bg-muted/10">
                      <td colSpan={weeks.length + 4} className="px-4 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <p className="font-semibold text-muted-foreground mb-1.5">Activity Types {selectedWeekIdx !== null ? `(${weeks[selectedWeekIdx]?.display})` : `(${parseInt(weekCount)}w)`}</p>
                            <div className="space-y-1">
                              {(selectedWeekIdx !== null
                                ? Object.entries(sp.weekly_type_breakdown?.[selectedWeekIdx] || {}).map(([type, count]) => ({ type, count })).sort((a,b)=>b.count-a.count)
                                : sp.type_breakdown
                              ).map(t => {
                                const breakdown = selectedWeekIdx !== null
                                  ? Object.entries(sp.weekly_type_breakdown?.[selectedWeekIdx] || {}).map(([type, count]) => ({ type, count })).sort((a,b)=>b.count-a.count)
                                  : sp.type_breakdown;
                                const maxCount = breakdown[0]?.count || 1;
                                const pct = Math.round(t.count / maxCount * 100);
                                return (
                                  <div key={t.type} className="flex items-center gap-2">
                                    <span className="w-24 truncate">{t.type}</span>
                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                      <div className="h-full rounded-full bg-[#800000]" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="font-semibold w-6 text-right">{t.count}</span>
                                  </div>
                                );
                              })}
                              {(selectedWeekIdx !== null && Object.keys(sp.weekly_type_breakdown?.[selectedWeekIdx] || {}).length === 0) && (
                                <p className="text-[11px] text-muted-foreground">No activity types for the selected week.</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="font-semibold text-muted-foreground mb-1.5">Weekly Trend</p>
                            <div className="flex items-end gap-0.5 h-14">
                              {sp.weekly_counts.map((c, i) => {
                                const maxC = Math.max(...sp.weekly_counts, weeklyTarget);
                                const h = maxC > 0 ? Math.max(2, Math.round(c / maxC * 48)) : 2;
                                const ht = maxC > 0 ? Math.round(weeklyTarget / maxC * 48) : 24;
                                const col = c >= weeklyTarget ? 'bg-emerald-500' : c > 0 ? 'bg-amber-400' : 'bg-gray-100';
                                return (
                                  <button type="button" key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${weeks[i]?.display}: ${c}`} onClick={() => setSelectedWeekIdx(selectedWeekIdx === i ? null : i)} data-testid={`weekly-trend-bar-${i}`}>
                                    <div className={`w-full rounded-sm ${col} ${selectedWeekIdx === i ? 'ring-2 ring-[#800000] ring-offset-1' : ''}`} style={{ height: h }} />
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-[9px] text-muted-foreground mt-1">{selectedWeekIdx !== null ? `Selected: ${weeks[selectedWeekIdx]?.display}` : `${weeks[0]?.display} → ${weeks[weeks.length-1]?.display}`}</p>
                          </div>
                          <div className="col-span-2 md:col-span-2">
                            <p className="font-semibold text-muted-foreground mb-1.5">Performance Summary</p>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { l: 'Total', v: sp.total, c: 'text-foreground' },
                                { l: 'Avg/week', v: sp.avg_per_week, c: sp.avg_per_week >= weeklyTarget ? 'text-emerald-600' : 'text-amber-600' },
                                { l: 'Target hit rate', v: `${sp.target_hit_rate_pct}%`, c: sp.target_hit_rate_pct >= 80 ? 'text-emerald-600' : 'text-amber-600' },
                                { l: 'Weeks on target', v: `${sp.weeks_on_target}/${parseInt(weekCount)}`, c: 'text-foreground' },
                                { l: 'Current week', v: sp.current_week, c: sp.current_week >= weeklyTarget ? 'text-emerald-600' : 'text-red-500' },
                                { l: 'vs Last week', v: sp.current_week - sp.prev_week >= 0 ? `+${sp.current_week - sp.prev_week}` : sp.current_week - sp.prev_week, c: sp.current_week >= sp.prev_week ? 'text-emerald-600' : 'text-red-500' },
                              ].map(m => (
                                <div key={m.l} className="p-2 rounded bg-muted/40">
                                  <p className="text-[10px] text-muted-foreground">{m.l}</p>
                                  <p className={`font-bold ${m.c}`}>{m.v}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {summary.length === 0 && (
                <tr><td colSpan={weeks.length + 4} className="text-center py-10 text-muted-foreground">
                  No activity data found for the selected period.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Q1 ASSIGNMENT PROGRESS PANEL ───────────────────────────── */}
      <Card className="border-[#800000]/20" data-testid="assignment-progress-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Link2 className="h-4 w-4 text-[#800000]" />
              Performance Hub Assignment Progress
              <span className="text-xs font-normal text-muted-foreground ml-1">
                Quarterly targets assigned in Performance Hub vs actual completions
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              {apData?.plans?.length > 0 && (
                <Select value={apPlanId || 'all'} onValueChange={v => setApPlanId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-52 h-7 text-xs"><SelectValue placeholder="All Plans" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    {apData.plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={loadAssignmentProgress}>
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {apLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : !apData?.assignments?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No assignments found</p>
              <p className="text-xs mt-1 text-muted-foreground/60">
                Assign activity targets to people in Performance Hub → Plan Builder → Assign
              </p>
            </div>
          ) : (
            <>
              {/* Person summary cards */}
              {apData.person_summary?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {apData.person_summary.map(ps => {
                    const pct = ps.overall_pct || 0;
                    const color = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500';
                    const bg = pct >= 80 ? 'bg-emerald-50 border-emerald-200' : pct >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
                    return (
                      <div key={ps.person} className={`p-3 rounded-lg border ${bg}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                            style={{ backgroundColor: avatarColor(ps.person) }}>
                            {initials(ps.person)}
                          </div>
                          <span className="text-xs font-medium truncate flex-1" title={ps.person}>
                            {ps.person.split(' ').slice(0, 2).join(' ')}
                          </span>
                        </div>
                        <div className="flex items-end justify-between">
                          <div>
                            <p className={`text-xl font-bold ${color}`}>{pct}%</p>
                            <p className="text-[10px] text-muted-foreground">{ps.total_actual}/{ps.total_target} completed</p>
                          </div>
                          {pct < 50 && <AlertTriangle className="h-4 w-4 text-red-400" />}
                          {pct >= 100 && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                        </div>
                        <Progress value={Math.min(pct, 100)} className="h-1 mt-1.5" />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Detailed assignment rows */}
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs" data-testid="assignment-progress-table">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Person</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Activity Type</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Plan / Period</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Target</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Actual</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Expected by Now</th>
                      <th className="px-3 py-2 font-semibold text-muted-foreground">Progress</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apData.assignments.map((a, i) => {
                      const pct = a.pct_complete || 0;
                      const isOnPace = a.on_pace;
                      const barColor = pct >= 100 ? 'bg-emerald-500' : isOnPace ? 'bg-emerald-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
                      const statusColor = pct >= 100 ? 'bg-emerald-100 text-emerald-700' : isOnPace ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700';
                      const statusLabel = pct >= 100 ? 'Complete' : isOnPace ? 'On Pace' : `Behind (−${a.pace_gap})`;

                      return (
                        <tr key={i} className="border-t hover:bg-muted/20" data-testid={`ap-row-${i}`}>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0"
                                style={{ backgroundColor: avatarColor(a.person) }}>
                                {initials(a.person)}
                              </div>
                              <span className="font-medium truncate max-w-28" title={a.person}>
                                {a.person.split(' ').slice(0,2).join(' ')}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${TYPE_COLORS[a.activity_type] || 'bg-gray-100 text-gray-600'}`}>
                              {a.activity_type}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="text-muted-foreground truncate max-w-40" title={a.plan_name}>
                              {a.plan_name ? <span>{a.plan_name}</span> : '—'}
                            </div>
                            <div className="text-[10px] text-muted-foreground/60">{a.period}</div>
                          </td>
                          <td className="px-3 py-2.5 text-center font-semibold">{a.target}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`font-bold ${a.actual >= a.target ? 'text-emerald-600' : a.actual > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {a.actual}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-center text-muted-foreground">
                            ~{a.expected_so_far}
                          </td>
                          <td className="px-3 py-2.5 w-32">
                            <div className="flex items-center gap-1.5">
                              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${barColor}`}
                                  style={{ width: `${Math.min(pct, 100)}%` }} />
                              </div>
                              <span className={`text-[10px] font-semibold w-8 text-right ${pct >= 100 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                                {pct}%
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusColor}`}>
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-[10px] text-muted-foreground mt-2 text-right">
                Targets from Performance Hub → Plan Builder → Assign · "Expected by Now" = pace target for current date in quarter
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
